import { BtnPrimary } from '../ui/components';
import { state } from '../store';
import { t } from '../i18n';
import { sanitizeHtml } from '../utils/sanitize';
import { currentPlayers } from './GameSetup';
import {
  loadSavedSettings, saveSettings, makeDefaultTeams,
  autoSplitPlayers, unassignedPlayers, loadMinigamePool, startTournament,
} from '../engine/TournamentEngine';
import type { TournamentTeam, MinigameId, TournamentSettings } from '../types/tournament';
import { TEAM_DEFAULTS, TEAM_EMOJI_POOL, TEAM_COLORS } from '../types/tournament';
import type { UnlockType } from '../types/content';
import { saveDifficulty } from '../api/client';

let teams: TournamentTeam[] = [];
let settings: TournamentSettings = loadSavedSettings();
let isStarting = false;
let isExpanded = false;  // collapsible state of "Tournament settings"

declare global {
  interface Window {
    trnAddTeam: () => void;
    trnRemoveTeam: (id: string) => void;
    trnAutoSplit: () => void;
    trnTogglePlayer: (playerId: string) => void;
    trnRenameTeam: (teamId: string, name: string) => void;
    trnCycleTeamEmoji: (teamId: string) => void;
    trnSetScoreTarget: (n: number) => void;
    trnSetTurnTimer: (sec: number) => void;
    trnToggleMinigame: (mg: MinigameId) => void;
    trnToggleCategory: (cat: UnlockType) => void;
    trnSetPunishment: (text: string) => void;
    trnSetDifficulty: (range: string | null) => void;
    trnToggleSettings: () => void;
    trnStart: () => void;
    trnRenderKeepScroll: () => void;
    trnAutoAssignNewPlayer: () => void;
    addPlayer: () => void;
  }
}

function rerenderSetupPreserveScroll(): void {
  const screen = document.getElementById('screen-tournament-setup');
  const prevTop = screen?.scrollTop ?? 0;

  window.renderApp();

  requestAnimationFrame(() => {
    const nextScreen = document.getElementById('screen-tournament-setup');
    if (nextScreen) nextScreen.scrollTop = prevTop;
  });
}

window.trnRenderKeepScroll = rerenderSetupPreserveScroll;

window.trnAutoAssignNewPlayer = () => {
  if (currentPlayers.length === 0) return;
  const newPlayer = currentPlayers[currentPlayers.length - 1];
  // Find smallest team (by player count)
  let minTeam = teams[0];
  for (const team of teams) {
    if (team.playerIds.length < minTeam.playerIds.length) minTeam = team;
  }
  if (minTeam && !minTeam.playerIds.includes(newPlayer.id)) {
    minTeam.playerIds.push(newPlayer.id);
  }
};

function colorClass(color: TournamentTeam['color']) { return TEAM_COLORS[color]; }

export function resetSetupState(): void {
  teams = makeDefaultTeams(2, t);
  settings = loadSavedSettings();
  teams = autoSplitPlayers(teams);
  isStarting = false;
  isExpanded = false;
}

window.trnAddTeam = () => {
  if (teams.length >= 4) return;
  const idx = teams.length;
  teams.push({
    id: 't' + (idx + 1),
    name: t('trn_setup_team_default').replace('{n}', String(idx + 1)),
    emoji: TEAM_DEFAULTS[idx].emoji,
    color: TEAM_DEFAULTS[idx].color,
    score: 0,
    playerIds: [],
  });
  rerenderSetupPreserveScroll();
};

window.trnRemoveTeam = (id: string) => {
  if (teams.length <= 2) return;
  teams = teams.filter(t => t.id !== id);
  rerenderSetupPreserveScroll();
};

window.trnAutoSplit = () => {
  teams = autoSplitPlayers(teams);
  rerenderSetupPreserveScroll();
};

window.trnTogglePlayer = (playerId: string) => {
  let currentTeam = -1;
  for (let i = 0; i < teams.length; i++) {
    if (teams[i].playerIds.includes(playerId)) { currentTeam = i; break; }
  }
  if (currentTeam >= 0) {
    teams[currentTeam].playerIds = teams[currentTeam].playerIds.filter(id => id !== playerId);
  }
  const nextTeam = currentTeam === -1 ? 0 : (currentTeam + 1) % (teams.length + 1);
  if (nextTeam < teams.length) {
    teams[nextTeam].playerIds.push(playerId);
  }
  rerenderSetupPreserveScroll();
};

window.trnRenameTeam = (teamId: string, name: string) => {
  const team = teams.find(t => t.id === teamId);
  if (team) team.name = name.slice(0, 30);
};

window.trnCycleTeamEmoji = (teamId: string) => {
  const team = teams.find(t => t.id === teamId);
  if (!team) return;
  const idx = TEAM_EMOJI_POOL.indexOf(team.emoji);
  const next = TEAM_EMOJI_POOL[(idx + 1) % TEAM_EMOJI_POOL.length];
  team.emoji = next;
  // DOM-only update — не теряем фокус с input'a имени.
  const btn = document.getElementById(`trn-team-emoji-${teamId}`);
  if (btn) btn.textContent = next;
};

window.trnSetScoreTarget = (n: number) => {
  if ([5,10,15,20].includes(n)) {
    settings.scoreTarget = n as 5 | 10 | 15 | 20;
    saveSettings(settings);
    rerenderSetupPreserveScroll();
  }
};

window.trnSetTurnTimer = (sec: number) => {
  if ([0,30,60,90].includes(sec)) {
    settings.turnDurationSec = sec as 0 | 30 | 60 | 90;
    saveSettings(settings);
    rerenderSetupPreserveScroll();
  }
};

window.trnToggleMinigame = (mg: MinigameId) => {
  const idx = settings.enabledMinigames.indexOf(mg);
  if (idx >= 0) {
    if (settings.enabledMinigames.length <= 1) return;
    settings.enabledMinigames.splice(idx, 1);
  } else {
    settings.enabledMinigames.push(mg);
  }
  saveSettings(settings);
  rerenderSetupPreserveScroll();
};

window.trnToggleCategory = (cat: UnlockType) => {
  const idx = settings.enabledCategories.indexOf(cat);
  if (idx >= 0) {
    if (settings.enabledCategories.length <= 1) return; // нельзя отключить все
    settings.enabledCategories.splice(idx, 1);
  } else {
    settings.enabledCategories.push(cat);
  }
  saveSettings(settings);
  rerenderSetupPreserveScroll();
};

window.trnSetPunishment = (text: string) => {
  settings.punishment = text.slice(0, 200);
  saveSettings(settings);
};

window.trnSetDifficulty = (range: string | null) => {
  state.difficultyRange = range;
  saveDifficulty(range);
  rerenderSetupPreserveScroll();
};

window.trnToggleSettings = () => {
  isExpanded = !isExpanded;
  rerenderSetupPreserveScroll();
};

window.trnStart = async () => {
  if (isStarting) return;

  // Проверка: у каждого назначенного игрока должно быть имя.
  // (Если добавили нового через addPlayer и забыли заполнить — подсветим.)
  const allUsedIds = new Set(teams.flatMap(t => t.playerIds));
  const emptyAssigned = currentPlayers.filter(p => allUsedIds.has(p.id) && !p.name.trim());
  if (emptyAssigned.length > 0) {
    window.showToast(t('setup_warning'), 'error');
    return;
  }

  const validTeams = teams.filter(t => t.playerIds.length > 0);
  if (validTeams.length < 2) {
    window.showToast(t('trn_setup_warn_teams'), 'error');
    return;
  }
  if (settings.enabledMinigames.length === 0) {
    window.showToast(t('trn_setup_warn_minigame'), 'error');
    return;
  }
  if (settings.enabledCategories.length === 0) {
    window.showToast(t('trn_setup_warn_no_levels'), 'error');
    return;
  }

  isStarting = true;
  const btn = document.getElementById('trn-start-btn');
  if (btn) {
    btn.style.pointerEvents = 'none';
    btn.innerHTML = '⏳ ' + t('trn_setup_loading');
  }

  try {
    const pool = await loadMinigamePool(settings.enabledMinigames, settings.enabledCategories);
    const hasAny = settings.enabledMinigames.some(id => (pool[id]?.cards.length ?? 0) > 0);
    if (!hasAny) {
      window.showToast(t('trn_setup_warn_no_levels'), 'error');
      isStarting = false;
      if (btn) btn.style.pointerEvents = 'auto';
      rerenderSetupPreserveScroll();
      return;
    }
    startTournament(teams, settings, pool);
    window.navTo('tournament-board');
  } catch (e) {
    console.error('Tournament start failed', e);
    isStarting = false;
    if (btn) {
      btn.style.pointerEvents = 'auto';
      btn.innerHTML = '🚀 ' + t('trn_setup_start');
    }
  }
};

// ─── Sub-renders ────────────────────────────────────────────────────

/** Карточка одной команды (с cycle-эмодзи и chip'ами игроков). */
function renderTeamCard(team: TournamentTeam, canRemove: boolean): string {
  const c = colorClass(team.color);
  const players = team.playerIds
    .map(id => currentPlayers.find(p => p.id === id))
    .filter((p): p is NonNullable<typeof p> => !!p);

  const playersHtml = players.map(p => `
    <span
      class="inline-flex items-center gap-[4px] px-[8px] py-[4px] rounded-pill bg-white/10 text-[12px] font-bold cursor-pointer active:scale-95 transition-transform"
      data-action="trn-toggle-player" data-player-id="${p.id}"
    >
      <span>${p.emoji}</span><span>${sanitizeHtml(p.name || '—')}</span>
    </span>
  `).join('');

  const removeBtn = canRemove
    ? `<span class="text-[18px] text-t-muted cursor-pointer p-[4px]" data-action="trn-remove-team" data-team-id="${team.id}">✕</span>`
    : '';

  return `
    <div class="${c.bg} border ${c.border} rounded-md p-[12px_14px] flex flex-col gap-[10px]">
      <div class="flex items-center gap-[10px]">
        <button
          id="trn-team-emoji-${team.id}"
          class="w-[40px] h-[40px] rounded-full bg-white/[0.08] flex items-center justify-center text-[26px] cursor-pointer shrink-0 border-none active:scale-90 transition-transform"
          data-action="trn-cycle-team-emoji" data-team-id="${team.id}"
          title="${t('trn_setup_team_emoji_hint')}"
        >${team.emoji}</button>
        <input
          class="flex-1 bg-transparent border-none outline-none font-display text-[16px] font-extrabold ${c.accent}"
          value="${sanitizeHtml(team.name)}"
          data-input-action="trn-rename-team" data-team-id="${team.id}"
          maxlength="30"
        />
        ${removeBtn}
      </div>
      <div class="flex flex-wrap gap-[6px] min-h-[28px]">
        ${playersHtml || `<span class="text-[12px] text-t-muted italic">—</span>`}
      </div>
    </div>
  `;
}

/** Секция "Игроки" — компактный список + кнопка добавления (использует window.addPlayer). */
function renderPlayersSection(): string {
  const playersHtml = currentPlayers.map(p => `
    <div class="flex items-center gap-[10px] p-[10px_12px] bg-glass border border-b-glass rounded-md">
      <button
        id="emoji-btn-${p.id}"
        class="w-[36px] h-[36px] rounded-full bg-white/[0.08] flex items-center justify-center text-[18px] cursor-pointer shrink-0 border-none"
        data-action="cycle-player-emoji" data-player-id="${p.id}"
      >${p.emoji}</button>
      <input
        data-player-input="${p.id}"
        class="flex-1 bg-transparent border-none outline-none font-body text-[14px] font-bold text-t-primary placeholder-t-muted"
        placeholder="${t('setup_player_placeholder')}"
        value="${sanitizeHtml(p.name)}"
        data-input-action="trn-update-player-name" data-player-id="${p.id}"
      />
      ${currentPlayers.length > 2 ? `
        <span class="text-[18px] text-t-muted cursor-pointer p-[4px]" data-action="trn-remove-player" data-player-id="${p.id}">✕</span>
      ` : ''}
    </div>
  `).join('');

  return `
    <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[8px] px-[20px] mt-[20px]">
      ${t('trn_setup_players_section')}
      <span class="text-t-muted text-[11px] font-normal">${t('trn_setup_players_section_sub')}</span>
    </div>
    <div class="px-[16px] flex flex-col gap-[6px]">
      ${playersHtml}
      <div
        class="flex items-center justify-center gap-[8px] p-[10px_12px] bg-transparent border-[1.5px] border-dashed border-white/[0.12] rounded-md cursor-pointer active:bg-white/[0.05]"
        data-action="trn-add-player"
      >
        <span class="text-[18px] text-t-muted">+</span>
        <span class="text-[13px] font-bold text-t-muted">${t('add_player')}</span>
      </div>
    </div>
  `;
}

/** Collapsible-секция всех настроек турнира. */
function renderSettingsSection(): string {

  // Score target
  const scoreBtns = ([5,10,15,20] as const).map(n => {
    const active = settings.scoreTarget === n;
    return `
      <div class="flex-1 p-[10px_8px] rounded-md border cursor-pointer text-center transition-all active:scale-95 ${active ? 'bg-neon-pink/15 border-neon-pink' : 'bg-glass border-transparent'}"
           data-action="trn-set-score-target" data-value="${n}">
        <div class="font-display text-[18px] font-extrabold ${active ? 'text-neon-pink' : 'text-white'}">${n}</div>
        <div class="text-[10px] font-bold ${active ? 'text-neon-pink' : 'text-t-muted'}">${t('trn_setup_score_pts')}</div>
      </div>
    `;
  }).join('');

  // Timer
  const timerOpts = [
    { v: 0,  label: t('trn_setup_timer_off') },
    { v: 30, label: t('trn_setup_timer_30') },
    { v: 60, label: t('trn_setup_timer_60') },
    { v: 90, label: t('trn_setup_timer_90') },
  ];
  const timerBtns = timerOpts.map(o => {
    const active = settings.turnDurationSec === o.v;
    return `
      <div class="flex-1 p-[10px_8px] rounded-md border cursor-pointer text-center transition-all active:scale-95 ${active ? 'bg-neon-blue/15 border-neon-blue' : 'bg-glass border-transparent'}"
           data-action="trn-set-turn-timer" data-value="${o.v}">
        <div class="text-[13px] font-extrabold ${active ? 'text-neon-blue' : 'text-white'}">${o.label}</div>
      </div>
    `;
  }).join('');

  // Minigame chips
  const mgList = [
    { id: 'wai' as MinigameId,      emoji: '🤔', label: t('trn_mg_wai') },
    { id: 'describe' as MinigameId, emoji: '💬', label: t('trn_mg_describe') },
    { id: 'act' as MinigameId,      emoji: '🙌', label: t('trn_mg_act') },
    { id: 'emoji' as MinigameId,    emoji: '😂', label: t('trn_mg_emoji') },
  ];
  const mgChips = mgList.map(mg => {
    const on = settings.enabledMinigames.includes(mg.id);
    return `
      <div class="flex-1 min-w-[140px] p-[10px_12px] rounded-md border cursor-pointer transition-all active:scale-95 flex items-center gap-[8px] ${on ? 'bg-neon-pink/10 border-neon-pink/40' : 'bg-glass border-white/10'}"
           data-action="trn-toggle-minigame" data-id="${mg.id}">
        <span class="text-[20px]">${mg.emoji}</span>
        <span class="text-[13px] font-bold ${on ? 'text-white' : 'text-t-muted'} flex-1">${mg.label}</span>
        <span class="text-[14px] ${on ? 'text-neon-pink' : 'text-t-muted'}">${on ? '✓' : '○'}</span>
      </div>
    `;
  }).join('');

  // Categories
  const catList: { id: UnlockType; emoji: string; label: string }[] = [
    { id: 'free',    emoji: '🆓', label: t('trn_setup_cat_free') },
    { id: 'coin',    emoji: '🪙', label: t('trn_setup_cat_coin') },
    { id: 'premium', emoji: '👑', label: t('trn_setup_cat_premium') },
    { id: 'invite',  emoji: '🤝', label: t('trn_setup_cat_invite') },
  ];
  const catChips = catList.map(c => {
    const on = settings.enabledCategories.includes(c.id);
    return `
      <div class="flex-1 min-w-[140px] p-[10px_12px] rounded-md border cursor-pointer transition-all active:scale-95 flex items-center gap-[8px] ${on ? 'bg-neon-blue/10 border-neon-blue/40' : 'bg-glass border-white/10'}"
           data-action="trn-toggle-category" data-id="${c.id}">
        <span class="text-[20px]">${c.emoji}</span>
        <span class="text-[13px] font-bold ${on ? 'text-white' : 'text-t-muted'} flex-1">${c.label}</span>
        <span class="text-[14px] ${on ? 'text-neon-blue' : 'text-t-muted'}">${on ? '✓' : '○'}</span>
      </div>
    `;
  }).join('');

  // Difficulty
  const difficultyOpts = (['easy','medium','hard','all'] as const).map(key => {
    const map: Record<string, string | null> = { easy:'easy', medium:'medium', hard:'hard', all:null };
    const icons: Record<string, string> = { easy:'😊', medium:'🤔', hard:'🧠', all:'🎲' };
    const range = map[key];
    const isActive = state.difficultyRange === range;
    const activeClass = isActive ? 'bg-neon-pink/15 border-neon-pink' : 'bg-glass border-transparent';
    const textClass = isActive ? 'text-neon-pink' : 'text-t-muted';
    return `
      <div class="flex-1 flex flex-col items-center gap-1 p-[10px_8px] rounded-md border cursor-pointer transition-all active:scale-95 ${activeClass}"
           data-action="trn-set-difficulty" data-range="${range ?? ''}">
        <span class="text-[18px]">${icons[key]}</span>
        <span class="text-[10px] font-bold ${textClass}">${t('difficulty_' + key)}</span>
      </div>
    `;
  }).join('');

  return `
    <!-- Score target -->
    <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[8px] px-[20px] mt-[16px]">${t('trn_setup_score_target')}</div>
    <div class="px-[16px] flex gap-[8px]">${scoreBtns}</div>

    <!-- Turn timer -->
    <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[8px] px-[20px] mt-[16px]">${t('trn_setup_turn_timer')}</div>
    <div class="px-[16px] flex gap-[8px]">${timerBtns}</div>

    <!-- Mini-games -->
    <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[8px] px-[20px] mt-[16px]">
      ${t('trn_setup_minigames')} <span class="text-t-muted text-[11px] font-normal">${t('trn_setup_minigames_sub')}</span>
    </div>
    <div class="px-[16px] flex flex-wrap gap-[8px]">${mgChips}</div>

    <!-- Categories -->
    <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[8px] px-[20px] mt-[16px]">
      ${t('trn_setup_categories')} <span class="text-t-muted text-[11px] font-normal">${t('trn_setup_categories_sub')}</span>
    </div>
    <div class="px-[16px] flex flex-wrap gap-[8px]">${catChips}</div>

    <!-- Difficulty -->
    <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[8px] px-[20px] mt-[16px]">
      ${t('trn_setup_difficulty')} <span class="text-t-muted text-[11px] font-normal">${t('trn_setup_difficulty_sub')}</span>
    </div>
    <div class="px-[16px] flex gap-[6px]">${difficultyOpts}</div>

    <!-- Punishment -->
    <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[8px] px-[20px] mt-[16px]">${t('trn_setup_punishment')}</div>
    <div class="px-[16px]">
      <textarea
        class="w-full bg-glass border border-b-glass rounded-md p-[12px_14px] text-[14px] font-semibold text-white placeholder-t-muted outline-none resize-none"
        rows="3"
        maxlength="200"
        placeholder="${t('trn_setup_punishment_placeholder')}"
        data-input-action="trn-set-punishment"
      >${sanitizeHtml(settings.punishment)}</textarea>
      <div class="text-[11px] text-t-muted font-semibold mt-[4px]">${t('trn_setup_punishment_sub')}</div>
    </div>
  `;
}

// ─── Main render ────────────────────────────────────────────────────

export function renderTournamentSetup(): string {
  const unassigned = unassignedPlayers(teams);

  // Авто-сплит при первом заходе если есть игроки и команды пустые
  const totalAssigned = teams.reduce((sum, t) => sum + t.playerIds.length, 0);
  if (totalAssigned === 0 && currentPlayers.filter(p => p.name.trim()).length > 0) {
    teams = autoSplitPlayers(teams);
  }

  const teamsHtml = teams.map(team => renderTeamCard(team, teams.length > 2)).join('');

  const unassignedHtml = unassigned.length > 0 ? `
    <div class="m-[16px_16px_0] p-[12px_14px] bg-white/5 border border-dashed border-white/15 rounded-md">
      <div class="text-[11px] font-extrabold tracking-[1px] text-t-muted mb-[6px]">${t('trn_setup_unassigned')}</div>
      <div class="text-[11px] text-t-muted mb-[8px]">${t('trn_setup_unassigned_sub')}</div>
      <div class="flex flex-wrap gap-[6px]">
        ${unassigned.map(p => `
          <span
            class="inline-flex items-center gap-[4px] px-[8px] py-[4px] rounded-pill bg-white/10 text-[12px] font-bold cursor-pointer active:scale-95"
            data-action="trn-toggle-player" data-player-id="${p.id}"
          ><span>${p.emoji}</span><span>${sanitizeHtml(p.name || '—')}</span></span>
        `).join('')}
      </div>
    </div>
  ` : '';

  return `
    <div class="absolute inset-0 overflow-y-auto overflow-x-hidden pb-[120px] z-10 scrollbar-none bg-void" id="screen-tournament-setup">
      <!-- Header -->
      <div class="p-[16px_16px_0] flex items-center gap-[12px]">
        <div class="w-[38px] h-[38px] bg-white/[0.07] border border-b-glass rounded-full flex items-center justify-center cursor-pointer text-[18px] active:scale-95 transition" data-action="nav" data-to="home">←</div>
        <div>
          <div class="text-[11px] text-t-muted font-bold tracking-[0.5px]">${t('setup_mode')}</div>
          <div class="font-display text-[20px] font-extrabold flex items-center gap-[6px]">🏆 ${t('trn_title')}</div>
        </div>
      </div>

      <!-- Players section (NEW) -->
      ${renderPlayersSection()}

      <!-- Teams section -->
      <div class="text-[12px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[8px] px-[20px] mt-[20px]">
        ${t('trn_setup_teams')} <span class="text-t-muted text-[11px] font-normal">${t('trn_setup_teams_sub')}</span>
      </div>
      <div class="px-[16px] flex flex-col gap-[10px]">
        ${teamsHtml}
        ${teams.length < 4 ? `
          <div class="flex items-center justify-center p-[12px_14px] bg-transparent border-[1.5px] border-dashed border-white/[0.12] rounded-md cursor-pointer active:bg-white/5 text-[14px] font-bold text-t-muted"
               data-action="trn-add-team">+ ${t('trn_setup_add_team')}</div>
        ` : ''}
      </div>
      ${unassignedHtml}
      <div class="px-[16px] mt-[10px]">
        <button class="w-full py-[10px] px-[16px] bg-glass border border-b-glass rounded-pill text-white text-[13px] font-bold active:scale-[0.97]" data-action="trn-auto-split">${t('trn_setup_auto_split')}</button>
      </div>

      <!-- Collapsible: Tournament settings -->
      <div class="px-[16px] mt-[20px]">
        <button
          class="w-full flex items-center justify-between p-[14px_16px] bg-glass border border-b-glass rounded-md cursor-pointer active:scale-[0.99] transition"
          data-action="trn-toggle-settings"
        >
          <div class="flex flex-col items-start text-left">
            <span class="text-[14px] font-extrabold text-white">⚙️ ${t('trn_setup_collapsed_label')}</span>
            <span class="text-[11px] text-t-muted font-semibold mt-[2px]">${t('trn_setup_collapsed_sub')}</span>
          </div>
          <span class="text-[18px] text-t-muted transition-transform" style="${isExpanded ? 'transform:rotate(180deg)' : ''}">▾</span>
        </button>
      </div>

      ${isExpanded ? renderSettingsSection() : ''}

      <!-- Launch -->
      <div class="px-[16px] mt-[24px]">
        ${BtnPrimary({ id: 'trn-start-btn', text: t('trn_setup_start'), onClick: 'trn-start' })}
      </div>

      <div class="h-[24px]"></div>
    </div>
  `;
}
